import { type FirebaseApp, getApps, initializeApp } from "firebase/app";
import {
  type Auth,
  type User as FirebaseUser,
  GoogleAuthProvider,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  signOut as fbSignOut,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  onAuthStateChanged,
  signInWithPopup,
  linkWithPopup,
  reauthenticateWithPopup,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
};

let _app: FirebaseApp | null = null;

export function getFirebaseApp(): FirebaseApp {
  if (_app) return _app;
  if (!firebaseConfig.apiKey) {
    throw new Error(
      "Firebase config missing. Set VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, VITE_FIREBASE_PROJECT_ID in apps/web/.env.local",
    );
  }
  _app = getApps()[0] ?? initializeApp(firebaseConfig);
  return _app;
}

let _auth: Auth | null = null;

/**
 * The Firebase Auth instance with EXPLICIT, durable persistence so a signed-in
 * user is remembered across reloads and return visits (the reported "it keeps
 * asking me to log in" bug). `getAuth()`'s implicit default can silently fall
 * back to in-memory when IndexedDB isn't the first thing available; here we pin
 * an explicit fallback chain: IndexedDB → localStorage → sessionStorage. We must
 * pass `browserPopupRedirectResolver` too, since `initializeAuth` (unlike
 * `getAuth`) doesn't wire the popup/redirect resolver that signInWithPopup needs.
 */
export function getFirebaseAuth(): Auth {
  if (_auth) return _auth;
  const app = getFirebaseApp();
  try {
    _auth = initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  } catch {
    // Already initialized (e.g. HMR / double-call) — reuse the existing instance.
    _auth = getAuth(app);
  }
  return _auth;
}

/** Allowed domains — wildcard allows any email */
export const ALLOWED_DOMAINS = ["*"] as const;
/** Backwards-compat label for screens */
export const ALLOWED_DOMAIN = "sellsynq.com";

export function isAllowedEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const at = email.lastIndexOf("@");
  return at > 0 && at < email.length - 1;
}

/** Result of a primary Google sign-in: the Firebase user plus the Google OAuth
 * access token captured at consent (scoped for BigQuery). The token is null only
 * when Google didn't return one (e.g. the user declined the extra scopes). */
export interface GoogleSignInResult {
  user: FirebaseUser;
  /** Google OAuth access token with bigquery + userinfo.email scopes, or null. */
  bqAccessToken: string | null;
}

export async function signInWithGoogle(): Promise<GoogleSignInResult> {
  const auth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  provider.addScope("email");
  provider.addScope("profile");
  provider.setCustomParameters({ prompt: "select_account" });

  const cred = await signInWithPopup(auth, provider);
  if (!isAllowedEmail(cred.user.email)) {
    await fbSignOut(auth);
    throw new Error("Invalid email address.");
  }
  const bqAccessToken = GoogleAuthProvider.credentialFromResult(cred)?.accessToken ?? null;
  return { user: cred.user, bqAccessToken };
}

/**
 * Admin-only, on-demand: request Drive read access and capture the Google OAuth
 * access token (the normal sign-in doesn't request Drive). Used to import
 * internal Google Docs into the knowledge base. Returns the access token.
 */
async function acquireGoogleOAuthToken(scopes: string[]): Promise<string> {
  const auth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  for (const s of scopes) {
    provider.addScope(s);
  }
  provider.setCustomParameters({ prompt: "select_account consent" });

  let cred;
  try {
    cred = await signInWithPopup(auth, provider);
  } catch (err: any) {
    if (auth.currentUser && (err?.code === "auth/account-exists-with-different-credential" || err?.code === "auth/credential-already-in-use")) {
      try {
        cred = await reauthenticateWithPopup(auth.currentUser, provider);
      } catch {
        cred = await linkWithPopup(auth.currentUser, provider);
      }
    } else {
      throw err;
    }
  }

  const credential = GoogleAuthProvider.credentialFromResult(cred);
  const token = credential?.accessToken;
  if (!token) {
    throw new Error("Google returned no access token (the requested permission may have been declined).");
  }
  return token;
}

export async function connectGoogleDrive(): Promise<string> {
  return acquireGoogleOAuthToken(["https://www.googleapis.com/auth/drive.readonly"]);
}

/**
 * Connect Google Cloud: re-consent with the cloud-platform scope and return the
 * OAuth access token. Used to mint a Gemini API key on the user's OWN GCP project
 * (with their access/billing) via the API Keys API.
 */
export async function connectGoogleCloud(): Promise<string> {
  const auth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  provider.addScope("https://www.googleapis.com/auth/cloud-platform");
  provider.setCustomParameters({ prompt: "consent" });
  const cred = await signInWithPopup(auth, provider);
  if (!isAllowedEmail(cred.user.email)) {
    await fbSignOut(auth);
    throw new Error("Invalid email address.");
  }
  const token = GoogleAuthProvider.credentialFromResult(cred)?.accessToken;
  if (!token) {
    throw new Error(
      "Google returned no Cloud access token (the Cloud Platform permission may have been declined or blocked).",
    );
  }
  return token;
}

/**
 * Connect BigQuery: re-consent with the bigquery + userinfo.email scopes and return
 * the OAuth access token. Used to call the CRM Company Report API, which runs the
 * billing/FinOps BigQuery query AS the user (under their own access). Mint fresh per
 * report call — tokens last ~1h and are never stored server-side.
 */
/**
 * Connect Google Calendar: request read-only access to calendar and return
 * the OAuth access token.
 */
export async function connectGoogleCalendar(): Promise<string> {
  return acquireGoogleOAuthToken(["https://www.googleapis.com/auth/calendar.readonly"]);
}

export async function connectBigQuery(): Promise<string> {
  const auth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  provider.addScope("https://www.googleapis.com/auth/bigquery");
  provider.addScope("https://www.googleapis.com/auth/userinfo.email");
  provider.setCustomParameters({ prompt: "consent" });
  const cred = await signInWithPopup(auth, provider);
  if (!isAllowedEmail(cred.user.email)) {
    await fbSignOut(auth);
    throw new Error("Invalid email address.");
  }
  const token = GoogleAuthProvider.credentialFromResult(cred)?.accessToken;
  if (!token) {
    throw new Error(
      "Google returned no BigQuery access token (the permission may have been declined or blocked).",
    );
  }
  return token;
}

export async function signOut(): Promise<void> {
  await fbSignOut(getFirebaseAuth());
}

export function onAuthChange(cb: (user: FirebaseUser | null) => void): () => void {
  return onAuthStateChanged(getFirebaseAuth(), cb);
}

// Resolves once Firebase has hydrated its auth state from persistence. Until
// this fires, `auth.currentUser` is null even for already-signed-in users.
let _authReady: Promise<void> | null = null;
function authReady(): Promise<void> {
  if (_authReady) return _authReady;
  _authReady = new Promise<void>((resolve) => {
    const auth = getFirebaseAuth();
    const unsub = onAuthStateChanged(auth, () => {
      unsub();
      resolve();
    });
  });
  return _authReady;
}

/**
 * Fresh Firebase ID token for the current user, or null if not signed in.
 * `forceRefresh` bypasses the SDK's cached token — used to recover from a 401
 * caused by an expired/edge-case token (see the api() retry) instead of nuking
 * the session and bouncing the user back to sign-in.
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  await authReady();
  const user = getFirebaseAuth().currentUser;
  return user ? user.getIdToken(forceRefresh) : null;
}
